import React from "react";
import {twJoin} from "tailwind-merge";
import {Alert, Button, defaultBorderColor} from "peggirkit";
import {OrderEditorTabProps} from "@widgets/pos/order/OrderEditor";
import ProductsInCart from "@widgets/pos/order/summary/products/ProductsInCart";
import OrderTotals from "@widgets/pos/order/summary/totals/OrderTotals";
import useOrderTotals from "@widgets/pos/order/hooks/UseOrderTotals";
import {useWatch} from "react-hook-form";
import {PosOrderForm} from "@/data/entities/order";
import {FormattedMessage} from "react-intl";
import {useExistingOrder} from "@widgets/pos/order/hooks/UseExistingOrder";
import CancelEdit from "@widgets/pos/order/summary/CancelEdit";

type Props = OrderEditorTabProps & {
    onSubmit: () => void,
};

const SummarySidebar = ({isPending, onSubmit}: Props) => {
    const orderTotals = useOrderTotals();
    const products = useWatch<PosOrderForm, "products">({name: "products"});
    const canSave: boolean = products.length > 0 && orderTotals.minimumOrder.status === "OK" && !isPending;
    const existingOrder = useExistingOrder();
    const isExistingOrder: boolean = existingOrder !== null;

    return (
        <section
            className={twJoin(
                "min-h-0 flex flex-col bg-white dark:bg-slate-900 border-l",
                defaultBorderColor,
            )}
        >
            <div className={twJoin("shrink-0 px-5 py-4 border-b font-medium text-lg", defaultBorderColor)}>
                <FormattedMessage id={"shoppingCart"}/>
            </div>

            <ProductsInCart isPending={isPending}/>

            {/* Totals & save button (always at the bottom) */}
            <div className={twJoin("shrink-0 px-5 py-4 border-t space-y-3", defaultBorderColor)}>
                <OrderTotals
                    orderTotals={orderTotals}
                    isPending={isPending}
                />

                <Alert
                    type={isExistingOrder ? "warning" : "primary"}
                    title={
                        <FormattedMessage
                            id={isExistingOrder ? "existingOrderReceiptPrintInfo" : "newOrderReceiptPrintInfo"}
                        />
                    }
                />

                <Button
                    size={"lg"}
                    width={"w-full"}
                    align={"center"}
                    disabled={!canSave}
                    loading={isPending}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: e => {
                                e.preventDefault();
                                onSubmit();
                            },
                        },
                    }}
                >
                    <FormattedMessage id={isExistingOrder ? "saveChanges" : "placeOrder"}/>
                </Button>

                <CancelEdit isPending={isPending}/>
            </div>
        </section>
    );
};

export default SummarySidebar;